import {forwardRef, useEffect, useState} from "react";
import {ISubject, TSubjectId} from "@/common/types";
import {IProtocol, isProtocol, TProtocolStatus} from "./types/protocol"
import {SubjectSelector} from "../common/components/SubjectSelector";
import {Affix, Box, BoxProps, Button, Loader, Text, TextInput} from "@mantine/core";
import {ProtocolSelector} from "../common/components/ProtocolSelector"
import classes from "./SubjectProtocolSelection.module.css"
import {useProtocolStore} from "./store/protocols";
import {useNeurotuneStore} from "../common/store/neurotune";
import cx from "clsx";
import {useProtocolDefinitionsStore} from "./store/protocolDefinitions";
import {IProtocolDefinition} from "./types/protocolDefinitions";

interface SubjectProtocolSelectionProps extends BoxProps {
    onProtocolSelection: (subjectId: TSubjectId, protocol: IProtocolDefinition | IProtocol, sessionName: string | null) => void
}

export const SubjectProtocolSelection = forwardRef<HTMLDivElement, SubjectProtocolSelectionProps>(({
                                                                                                       onProtocolSelection,
                                                                                                       ...others
                                                                                                   }, ref) => {
        const activeCompany = useNeurotuneStore((state) => state.activeCompany)
        const subjects = useNeurotuneStore((state) => state.subjects)
        const loadSubjects = useNeurotuneStore((state) => state.loadSubjects)
        const loadingProtocolDefinitions = useProtocolDefinitionsStore((state) => state.protocolDefinitionsLoading)
        const loadingProtocols = useProtocolStore((state) => state.protocolsLoading)
        const loadProtocolDefinitions = useProtocolDefinitionsStore((state) => state.loadProtocolDefinitions)
        const loadProtocols = useProtocolStore((state) => state.loadProtocols)
        const protocolDefinitions = useProtocolDefinitionsStore((state) => state.protocolDefinitions)
        const [selectedSubject, setSelectedSubject] = useState<ISubject>()
        const [selectedProtocol, setSelectedProtocol] = useState<IProtocol | IProtocolDefinition>()
        const [sessionName, setSessionName] = useState<string>("")
        const protocols = useProtocolStore((state) => state.protocols).filter((protocol) => (
                protocol.subjectId === selectedSubject?.id
            )
        )
        const renderLoadingSavingStatus = () => {
            return (
                <Affix position={{bottom: 20, right: 20}}>
                    {loadingProtocols || loadingProtocolDefinitions ? <Loader color="var(--bios-strong-accent)"/> : <></>}
                </Affix>
            )
        }
        const renderStartButton = (): String => {
            if (selectedProtocol && isProtocol(selectedProtocol))
                return "Resume Protocol"
            else
                return "Start Protocol"
        }

        useEffect(() => {
            loadProtocolDefinitions(activeCompany.id)
        }, [])

        const onSubjectSelected = (subject: ISubject) => {
            setSelectedSubject(subject)
            loadProtocols(activeCompany.id, {subjectId: subject.id})
        }
        const onContinue = () => {
            if (selectedSubject && selectedProtocol) {
                onProtocolSelection(selectedSubject.id, selectedProtocol, isProtocol(selectedProtocol) ? null : sessionName)
            }
        }
        return (
            <Box ref={ref} className={cx(classes.root)} {...others}>
                <div>
                    <Text fw={"bolder"} className="ntc-flex-justify-center ntc-flex-align-center">Select a
                        Subject and
                        Protocol for Collection</Text>
                    <hr style={{width: "100%"}}/>
                </div>
                <div className={"ntc-maximum-row-component"}>
                    <SubjectSelector data-testid={"subject-selector"} w="20rem" subjects={subjects}
                                     selectedId={selectedSubject?.id}
                                     onSelection={onSubjectSelected}
                    />
                    <ProtocolSelector data-testid={"protocol-selector"} w="20rem" ml={"0.5rem"}
                                      disabled={!selectedSubject}
                                      protocols={protocols}
                                      protocolDefinitions={protocolDefinitions}
                                      selectedProtocol={selectedProtocol}
                                      onProtocolSelection={(sp) => {
                                          setSelectedProtocol(sp)
                                          if (!isProtocol(sp)) {
                                              setSessionName(`${sp.name} ${protocols.length > 0 ? `#${protocols.length + 1}` : ""}`)
                                          }
                                      }}/>
                </div>
                <Box style={{display: "grid", grid: "auto / auto auto", alignItems: "end", columnGap: "0.5em"}}>
                    {(!selectedProtocol || isProtocol(selectedProtocol)) ? "" :
                        <TextInput label={"Set Session Name"}
                                   value={sessionName} onChange={(value) => {
                            setSessionName(value.currentTarget.value)
                        }}/>
                    }
                    <Button data-testid={"btn-continue"} disabled={!selectedProtocol || !selectedSubject}
                            onClick={onContinue}>{renderStartButton()}</Button>
                </Box>
                {renderLoadingSavingStatus()}
            </Box>
        )
    }
)